import { useState, useEffect } from 'react';
import './CookieConsent.css';
import { useTranslation } from '../i18n/LanguageContext.jsx';
import { grantConsent, revokeConsent } from '../analytics.js';

/**
 * Cookie consent banner component (GDPR compliant).
 *
 * Fixed bottom bar that asks users to accept or decline cookies.
 * Persists choice in localStorage under 'cookie-consent' key.
 * On accept, calls grantConsent() to update GA4 Consent Mode v2.
 * On reject (after prior grant), calls revokeConsent() to withdraw consent.
 *
 * @param {Object} props
 * @param {Function} [props.onPrivacyClick] - Callback to open privacy policy modal
 * @param {boolean} [props.forceOpen] - Force banner to show (e.g., from cookie settings link)
 * @param {Function} [props.onSettingsClose] - Callback when settings interaction completes
 */
function CookieConsent({ onPrivacyClick, forceOpen, onSettingsClose }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(() => {
    try {
      const stored = localStorage.getItem('cookie-consent');
      // Only treat 'granted' or 'denied' as valid; show banner for any other value
      if (stored && stored !== 'granted' && stored !== 'denied') {
        localStorage.removeItem('cookie-consent');
        return true;
      }
      return !stored;
    } catch (e) {
      console.warn('localStorage access failed, showing consent banner:', e);
      return true;
    }
  });

  // Re-show banner when forceOpen becomes true
  useEffect(() => {
    if (forceOpen) {
      setVisible(true); // eslint-disable-line react-hooks/set-state-in-effect -- syncing prop to local state is intentional
    }
  }, [forceOpen]);

  const handleAccept = () => {
    try {
      localStorage.setItem('cookie-consent', 'granted');
    } catch (e) {
      console.warn('localStorage write failed:', e);
    }
    grantConsent();
    setVisible(false);
    if (onSettingsClose) onSettingsClose();
  };

  const handleReject = () => {
    let wasGranted = false;
    try {
      wasGranted = localStorage.getItem('cookie-consent') === 'granted';
      localStorage.setItem('cookie-consent', 'denied');
    } catch (e) {
      console.warn('localStorage access failed:', e);
    }
    if (wasGranted) {
      revokeConsent();
    }
    setVisible(false);
    if (onSettingsClose) onSettingsClose();
  };

  if (!visible) return null;

  return (
    <div className="cookie-consent" role="dialog" aria-label={t('cookieConsent.ariaLabel')}>
      <div className="cookie-consent-content">
        <p className="cookie-consent-message">
          {t('cookieConsent.message')}
          {onPrivacyClick && (
            <>
              {' '}
              <button
                className="cookie-consent-privacy-link"
                onClick={onPrivacyClick}
                type="button"
              >
                {t('cookieConsent.privacyLink')}
              </button>
            </>
          )}
        </p>
        <div className="cookie-consent-actions">
          <button className="cookie-consent-accept" onClick={handleAccept} type="button">
            {t('cookieConsent.accept')}
          </button>
          <button className="cookie-consent-reject" onClick={handleReject} type="button">
            {t('cookieConsent.reject')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
